
import { Procurement } from '@/types/database.types';
import { calculateTotalAmount, calculateNetPayableKg, calculateNetPayableAmount, getWarehouseReceivedKg, getQualityCompensationKg } from '@/utils/procurementCalculations';
import { 
  getProductsSummary, 
  formatRequestorInfo, 
  formatApproverInfo, 
  formatRejectorInfo, 
  formatSourceInfo, 
  formatPaymentInfo,
  formatQualityInfo
} from './utils';

/**
 * Format a basic procurement notification message
 */
export const formatNewProcurementNotification = (procurement: Procurement): string => {
  const productInfo = getProductsSummary(procurement);
  const requestorInfo = formatRequestorInfo(procurement.requestor);
  const sourceInfo = formatSourceInfo(procurement);
  const paymentInfo = formatPaymentInfo(procurement);
  
  return `🆕 *New Procurement Created*

ID: ${procurement.id.substring(0, 8)}
${productInfo}
Total Quantity: ${procurement.total_quantity || 0} kg
Total Amount: Br${calculateTotalAmount(procurement).toFixed(2)}

📍 Source: ${sourceInfo}
${requestorInfo}
${paymentInfo}

To view details, use command:
/status ${procurement.id.substring(0, 8)}`;
};

/**
 * Format an approved procurement notification message
 * Uses finance snapshot values for accurate net payable display
 */
export const formatApprovedProcurementNotification = (procurement: Procurement, approver?: any): string => {
  const productInfo = getProductsSummary(procurement);
  const requestorInfo = formatRequestorInfo(procurement.requestor);
  const approverInfo = formatApproverInfo(approver);
  const paymentInfo = formatPaymentInfo(procurement);
  const qualityInfo = formatQualityInfo(procurement);
  
  // Use finance snapshot values if available, otherwise calculate
  const netPayableKg = procurement.finance?.approved_net_payable_kg || calculateNetPayableKg(procurement);
  const netPayableAmount = procurement.finance?.approved_net_payable_amount || calculateNetPayableAmount(procurement);
  
  // Determine payment type for messaging
  const paymentType = procurement.finance?.payment_term_type || 'upfront';
  const amountLabel = paymentType === 'upfront' ? 'Amount Paid' : 'Amount Approved';
  
  return `✅ *Procurement Approved${paymentType === 'upfront' ? ' & Paid' : ''}*

ID: ${procurement.id.substring(0, 8)}
${productInfo}
${qualityInfo}
📊 Net Payable: ${netPayableKg.toFixed(2)} kg
💰 ${amountLabel}: Br${netPayableAmount.toFixed(2)}

${requestorInfo}${approverInfo ? '\n' + approverInfo : ''}
${paymentInfo}

To view details, use command:
/status ${procurement.id.substring(0, 8)}`;
};

/**
 * Format a rejected procurement notification message
 */
export const formatRejectedProcurementNotification = (procurement: Procurement, rejector?: any, reason?: string): string => {
  const productInfo = getProductsSummary(procurement);
  const requestorInfo = formatRequestorInfo(procurement.requestor);
  const rejectorInfo = formatRejectorInfo(rejector);
  const qualityInfo = formatQualityInfo(procurement);
  
  let message = `❌ *Procurement Rejected*

ID: ${procurement.id.substring(0, 8)}
${productInfo}
Total Quantity: ${procurement.total_quantity || 0} kg
Total Amount: Br${calculateTotalAmount(procurement).toFixed(2)}
${qualityInfo}
${requestorInfo}${rejectorInfo ? '\n' + rejectorInfo : ''}`;

  if (reason) {
    message += `\n📝 Reason: ${reason}`;
  }

  message += `\n\nTo view details, use command:
/status ${procurement.id.substring(0, 8)}`;

  return message;
};

/**
 * Format a status change notification message
 * Shows warehouse data for warehouse updates
 */
export const formatStatusChangeNotification = (procurement: Procurement, oldStatus: string, newStatus: string): string => {
  const productInfo = getProductsSummary(procurement);
  const requestorInfo = formatRequestorInfo(procurement.requestor);
  const paymentInfo = formatPaymentInfo(procurement);
  
  // Determine if this is a warehouse status update
  const isWarehouseUpdate = oldStatus === 'pending_warehouse' || 
    (oldStatus === 'pending_warehouse' && (newStatus === 'pending_finance' || newStatus === 'completed'));
  
  const title = isWarehouseUpdate ? '🏭 *Warehouse Status Update*' : '🔄 *Procurement Status Updated*';
  
  // Build warehouse/net payable section for warehouse updates
  let quantitySection = '';
  if (isWarehouseUpdate) {
    const receivedKg = getWarehouseReceivedKg(procurement);
    const compensationKg = getQualityCompensationKg(procurement);
    const netPayableKg = calculateNetPayableKg(procurement);
    const netPayableAmount = calculateNetPayableAmount(procurement);
    
    quantitySection = `\n📦 Received: ${receivedKg.toFixed(2)} kg`;
    if (compensationKg > 0) {
      quantitySection += `\n⚠️ Compensation: ${compensationKg.toFixed(2)} kg`;
    }
    quantitySection += `\n💰 Net Payable: ${netPayableKg.toFixed(2)} kg (Br${netPayableAmount.toFixed(2)})`;
  } else {
    quantitySection = `\nTotal Quantity: ${procurement.total_quantity || 0} kg\nTotal Amount: Br${calculateTotalAmount(procurement).toFixed(2)}`;
  }
  
  return `${title}

ID: ${procurement.id.substring(0, 8)}
${productInfo}
${quantitySection}

Status: ${oldStatus} → ${newStatus}
${requestorInfo}
${paymentInfo}

To view details, use command:
/status ${procurement.id.substring(0, 8)}`;
};
